import { useSelector, shallowEqual } from 'react-redux';
import { ArrowLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { api, useGetConversationsQuery } from '@/services/ApiService';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { ConversationHistoryItem } from './ConversationItem';
import { AI_CHAT_SIDEBAR_DRAWER_WIDTH } from './SidebarDrawer';
import type { AppStore } from '@/state/Store';

type RootState = ReturnType<AppStore['getState']>;

interface ConversationSidebarProps {
    currentConversationId: string | null;
    onSelectConversation: (conversationId: string) => void;
    onNewConversation: () => void;
    onClose?: () => void;
}

export const ConversationSidebar = ({
    currentConversationId,
    onSelectConversation,
    onNewConversation,
    onClose,
}: ConversationSidebarProps) => {
    const isDesktop = useIsDesktop();
    const { data: conversationsData, isLoading } = useGetConversationsQuery();
    const conversationIds = conversationsData?.conversationIds ?? [];

    const sortedConversationIds = useSelector(
        (state: RootState) => {
            if (conversationIds.length === 0) return conversationIds;
            return [...conversationIds].sort((a, b) => {
                const historyA = api.endpoints.getConversationHistory.select(a)(state);
                const historyB = api.endpoints.getConversationHistory.select(b)(state);
                const msgsA = historyA?.data?.messages;
                const msgsB = historyB?.data?.messages;
                const lastMsgA = msgsA?.length ? msgsA[msgsA.length - 1] : undefined;
                const lastMsgB = msgsB?.length ? msgsB[msgsB.length - 1] : undefined;
                const timeA = lastMsgA?.createdAt ? new Date(lastMsgA.createdAt).getTime() : 0;
                const timeB = lastMsgB?.createdAt ? new Date(lastMsgB.createdAt).getTime() : 0;
                return timeB - timeA;
            });
        },
        shallowEqual,
    );

    return (
        <div
            className={cn(
                'flex h-full min-h-0 min-w-0 w-full flex-col bg-secondary-dark/50 light:bg-card/50 dark:bg-card/50',
                isDesktop
                && 'shadow-[inset_-10px_0_14px_-6px_rgb(0_0_0_/_0.14)] light:shadow-[inset_-10px_0_14px_-6px_rgb(0_0_0_/_0.08)] dark:shadow-[inset_-10px_0_18px_-6px_rgb(0_0_0_/_0.22)]',
            )}
        >
            {!isDesktop && onClose && (
                <div className="flex items-center gap-2 p-3 border-b border-border">
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        size="icon"
                        className="text-white light:text-foreground dark:text-foreground"
                        data-track-click="AI Chat Sidebar Back Clicked"
                        data-track-location="ai-chat"
                        data-track-button-name="ai-chat-sidebar-back"
                    >
                        <ArrowLeft className="size-4" />
                    </Button>
                    <span className="text-sm font-medium text-white light:text-foreground dark:text-foreground">Conversations</span>
                </div>
            )}
            <div className="p-3 mt-[10px]">
                <Button
                    onClick={onNewConversation}
                    size="sm"
                    variant="outline"
                    className="justify-start gap-2 text-white light:text-foreground dark:text-foreground"
                    data-track-click="AI Chat Sidebar New Chat Clicked"
                    data-track-location="ai-chat"
                    data-track-button-name="ai-chat-sidebar-new-chat"
                >
                    <Plus className="size-4" />
                    New chat
                </Button>
            </div>

            <ScrollArea
                data-scroll-constrain=""
                className={cn(
                    'min-h-0 flex-1',
                    isDesktop
                        ? cn(AI_CHAT_SIDEBAR_DRAWER_WIDTH, 'shrink-0')
                        : 'min-w-0 w-full max-w-full',
                )}
            >
                <div className="flex min-w-0 flex-col gap-1 p-2">
                    {isLoading
                        ? (
                                <div className="flex flex-col gap-2 p-2">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="animate-pulse">
                                            <div className="h-4 bg-secondary/50 rounded w-3/4 mb-1" />
                                            <div className="h-3 bg-secondary/50 rounded w-1/2" />
                                        </div>
                                    ))}
                                </div>
                            )
                        : conversationIds.length === 0
                            ? (
                                    <p className="text-sm text-muted-foreground p-3 text-center">
                                        No conversations yet
                                    </p>
                                )
                            : (
                                    sortedConversationIds.map(id => (
                                        <ConversationHistoryItem
                                            key={id}
                                            conversationId={id}
                                            isActive={id === currentConversationId}
                                            onClick={() => onSelectConversation(id)}
                                        />
                                    ))
                                )}
                </div>
            </ScrollArea>
        </div>
    );
};
